import { loadStripe, type Stripe } from '@stripe/stripe-js';

import { webConfig } from '@gitbook/env';
import { createCacheSingleton } from '@gitbook/util-suspense-cache';

const stripeCache = createCacheSingleton<string, Stripe>(async (publishKey) => {
    // Stripe may fail loading in poor or absent network conditions
    // https://github.com/stripe/stripe-js/issues/26
    async function loadStripeWithBackoffRetries(
        retries: number,
        delay: number
    ): ReturnType<typeof loadStripe> {
        try {
            return await loadStripe(publishKey);
        } catch (error) {
            if (retries <= 0) {
                throw error;
            }
            await new Promise((resolve) => setTimeout(resolve, delay));
            return loadStripeWithBackoffRetries(retries - 1, delay * 2);
        }
    }

    const stripe = await loadStripeWithBackoffRetries(3, 1000);
    if (!stripe) {
        throw new Error('Failed to load Stripe. It cannot be loaded in a server environment');
    }

    return stripe;
});

/**
 * Redirects the user to Stripe's checkout with the given session id
 */
export function useRedirectToStripe() {
    const stripe = stripeCache.read(webConfig.stripe.public_key);

    return async (stripeSessionId: string) => {
        await stripe.redirectToCheckout({
            sessionId: stripeSessionId,
        });
    };
}
